import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { api, type Page } from '../lib/api';
import { Button, TextField } from '../ui/controls';
import { ConfirmDialog, Dialog } from '../ui/dialog';
import { ErrorNotice, Notice, useToast } from '../ui/feedback';
import { DataTable, PageHead, Pager, useKeysetPage } from '../ui/table';
import { Mono, Num, Tag, Time } from '../ui/values';
import { useUrlFilter } from './common';

/** Beta-Codes (design.md §6): Bezeichnung · genutzt/max · läuft ab · Status · erstellt von. Klartext nur einmal (§9). */

type CodeState = 'active' | 'revoked' | 'expired' | 'exhausted';
interface BetaCode {
  id: string;
  label: string | null;
  maxUses: number;
  useCount: number;
  state: CodeState;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt: string;
  createdBy: { id: string; name: string | null } | null;
}

const STATE: Record<CodeState, ['ok' | 'warn' | 'neutral', string]> = {
  active: ['ok', 'aktiv'],
  exhausted: ['warn', 'aufgebraucht'],
  expired: ['neutral', 'abgelaufen'],
  revoked: ['neutral', 'widerrufen'],
};

export function BetaCodesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const page = useKeysetPage();
  const [state, setState] = useUrlFilter('state');
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<BetaCode | null>(null);
  const query = { state, cursor: page.cursor, limit: page.limit };
  const list = useQuery({
    queryKey: ['admin', 'beta', query],
    queryFn: () => api.get<Page<BetaCode>>('/admin/beta-codes', query),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="stack">
      <PageHead
        title="Beta-Codes"
        context="Spieler lösen einen Code nach dem ersten Login ein."
        actions={
          <Button variant="primary" icon={<Plus aria-hidden />} onClick={() => setCreating(true)}>
            Code erstellen
          </Button>
        }
      />
      <div className="filters">
        <select aria-label="Status" value={state} onChange={(e) => setState(e.target.value)}>
          <option value="">Alle Status</option>
          <option value="active">aktiv</option>
          <option value="exhausted">aufgebraucht</option>
          <option value="expired">abgelaufen</option>
          <option value="revoked">widerrufen</option>
        </select>
      </div>
      {list.isError && <ErrorNotice error={list.error} onRetry={() => list.refetch()} />}
      <DataTable
        label="Beta-Codes"
        rows={list.data?.items}
        loading={list.isPending}
        rowKey={(c) => c.id}
        empty={state ? 'Keine Codes mit diesem Status.' : 'Noch keine Beta-Codes. „Code erstellen“ legt den ersten an.'}
        columns={[
          { key: 'label', header: 'Bezeichnung', cell: (c) => c.label ?? <span className="muted">ohne Bezeichnung</span> },
          {
            key: 'uses',
            header: 'genutzt/max',
            num: true,
            cell: (c) => (
              <>
                <Num value={c.useCount} /> / <Num value={c.maxUses} />
              </>
            ),
          },
          { key: 'exp', header: 'läuft ab', cell: (c) => (c.expiresAt ? <Time iso={c.expiresAt} absolute /> : <span className="muted">nie</span>) },
          { key: 'state', header: 'Status', cell: (c) => <Tag tone={STATE[c.state][0]}>{STATE[c.state][1]}</Tag> },
          { key: 'by', header: 'erstellt von', hide: 'sm', cell: (c) => c.createdBy?.name ?? <span className="muted">–</span> },
          {
            key: 'act',
            header: '',
            num: true,
            cell: (c) =>
              c.state === 'revoked' ? null : (
                <Button variant="danger" className="compact" onClick={() => setRevoking(c)}>
                  Widerrufen…
                </Button>
              ),
          },
        ]}
      />
      <Pager page={page} count={list.data?.items.length ?? 0} nextCursor={list.data?.nextCursor} />

      {creating && (
        <CreateDialog
          onClose={() => setCreating(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['admin', 'beta'] })}
        />
      )}
      {revoking && (
        <ConfirmDialog
          open
          title="Beta-Code widerrufen"
          consequence={`Der Code ${revoking.label ? `„${revoking.label}“ ` : ''}kann nicht mehr eingelöst werden. Bereits freigeschaltete Spieler (${revoking.useCount}) bleiben freigeschaltet.`}
          confirmText={revoking.label ?? undefined}
          confirmLabel="Widerrufen"
          onConfirm={async (reason) => {
            await api.post(`/admin/beta-codes/${revoking.id}/revoke`, { reason });
            await qc.invalidateQueries({ queryKey: ['admin', 'beta'] });
            toast('Code widerrufen.');
          }}
          onClose={() => setRevoking(null)}
        />
      )}
    </div>
  );
}

/** Erstellen; danach zeigt derselbe Dialog den Klartext einmalig an. */
function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [label, setLabel] = useState('');
  const [maxUses, setMaxUses] = useState('1');
  const [expires, setExpires] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [created, setCreated] = useState<string | null>(null);
  const uses = Number(maxUses);
  const valid = Number.isInteger(uses) && uses >= 1 && uses <= 10_000;

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      // Datum = Ende des gewählten Tages in lokaler Zeit
      const expiresAt = expires ? new Date(`${expires}T23:59:59`).toISOString() : undefined;
      const res = await api.post<{ code: string }>('/admin/beta-codes', { label: label.trim() || undefined, maxUses: uses, expiresAt });
      setCreated(res.code);
      onCreated();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <Dialog open title="Beta-Code erstellt" onClose={onClose} footer={
          // eigener key: sonst übernimmt React den Knoten von „Abbrechen“ und blendet dessen Farbe über
          <Button key="done" variant="primary" onClick={onClose}>
            Fertig
          </Button>
        }>
        <p className="code-big">
          <Mono value={created} />
        </p>
        <Notice tone="warn">Der Code wird nur jetzt angezeigt. Kopiere ihn, bevor du den Dialog schließt.</Notice>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      title="Beta-Code erstellen"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={create} busy={busy} busyText="Wird erstellt…" disabled={!valid}>
            Erstellen
          </Button>
        </>
      }
    >
      <TextField label="Bezeichnung" value={label} maxLength={100} onChange={(e) => setLabel(e.target.value)} help="Wofür der Code ist, z. B. „Discord-Runde 1“." wide autoFocus />
      <TextField
        label="Maximale Nutzungen"
        type="number"
        min={1}
        max={10000}
        value={maxUses}
        onChange={(e) => setMaxUses(e.target.value)}
        error={valid ? null : 'Bitte eine Zahl von 1 bis 10.000 eingeben.'}
      />
      <TextField label="Läuft ab am" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} help="Leer lassen: läuft nicht ab." />
      {error !== null && <ErrorNotice error={error} />}
    </Dialog>
  );
}
